import { useEffect, useMemo, useState } from "react"
import { useTranslation } from "@/clientWeb/i18n/LanguageContext"
import { Phone, Video, PhoneIncoming, PhoneMissed, PhoneOutgoing, PhoneCall, Search, X, ArrowLeft } from "lucide-react"
import { useCallList } from "./hooks/useCallList"
import type { CallRecord } from "./types"
import { CallInfoPanel } from "./call-info-panel"
import { useCallsStore } from "./store/calls.store"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/clientWeb/components/ui/dialog"
import type { SearchScope } from "@/features/meetings/services/contacts.api"
import { useAuthStore } from "@/features/auth/store/auth.store"
import { useDirectorySearch } from "./hooks/useDirectorySearch"
import { DirectoryScopeTabs } from "./directory-scope-tabs"
import { LoadMoreSentinel } from "./load-more-sentinel"
import { AvatarPhoto } from "./avatar-photo"

type CallablePerson = { id: string; name: string; subtitle: string; contactOrgName?: string; imageUrl?: string }

function Avatar({ name, imageUrl, contactId, size = 44 }: { name: string; imageUrl?: string; contactId?: string; size?: number }) {
  const initials = name.split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase()
  const colors = ["bg-primary/20 text-primary", "bg-violet-500/20 text-violet-600", "bg-emerald-500/20 text-emerald-600", "bg-amber-500/20 text-amber-600"]
  const colorIdx = name.charCodeAt(0) % colors.length
  return (
    <div className={`rounded-full flex items-center justify-center text-sm font-semibold shrink-0 overflow-hidden ${colors[colorIdx]}`} style={{ width: size, height: size }}>
      <AvatarPhoto src={imageUrl} alt={name} className="w-full h-full object-cover" fallback={initials} contactId={contactId} />
    </div>
  )
}

function CallIcon({ record }: { record: CallRecord }) {
  if (record.isMissedCall) return <PhoneMissed className="w-4 h-4 text-destructive" />
  if (record.callDir === "out") return <PhoneOutgoing className="w-4 h-4 text-muted-foreground" />
  if (record.isVideoCall) return <Video className="w-4 h-4 text-muted-foreground" />
  return <PhoneIncoming className="w-4 h-4 text-green-500" />
}

interface CallsTabProps {
  /**
   * Open the chat with a caller. Supplied by PresencePage, which owns both the
   * DM-create path and the selected conversation — a tab cannot navigate to a
   * conversation on its own.
   */
  onOpenChat?: (contact: { id: string; name: string; imageUrl?: string }) => void
}

export function CallsTab({ onOpenChat }: CallsTabProps = {}) {
  const t = useTranslation()
  const [search, setSearch] = useState("")
  const [isSearching, setIsSearching] = useState(false)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [showNewCall, setShowNewCall] = useState(false)

  /**
   * Who you can call, from the org directory.
   *
   * This dialog used to list the call HISTORY, which meant "New Call" could only
   * ever redial somebody you had already spoken to — and showed nothing at all
   * to anyone whose history was empty, which is every new user. A picker that
   * cannot reach a person you have not called yet is not a picker.
   *
   * Loaded lazily, only while the dialog is open, and debounced — the same
   * shape the New Chat and New Group pickers use against the same endpoint, so
   * the ids that come back are contactPresenceIds and `startCall` accepts them
   * directly.
   */
  const myUserId = useAuthStore((s) => s.presenceId)
  const [peopleQuery, setPeopleQuery] = useState("")
  const [peopleScope, setPeopleScope] = useState<SearchScope>("global")

  // Reset the picker's own state each time it opens, same as New Chat / New
  // Group: it always starts on Global with an empty query.
  useEffect(() => {
    if (!showNewCall) { setPeopleQuery(""); setPeopleScope("global") }
  }, [showNewCall])

  const {
    contacts: directoryPeople,
    loading: peopleLoading,
    loadingMore: peopleLoadingMore,
    hasMore: peopleHasMore,
    loadMore: loadMorePeople,
  } = useDirectorySearch({ enabled: showNewCall && !!myUserId, scope: peopleScope, query: peopleQuery, pageSize: 50 })

  // The directory row is already shaped close enough to what this dialog
  // renders; `startCall` just needs the id and a label.
  const people: CallablePerson[] = directoryPeople.map((row) => ({
    id: row.id,
    name: row.name,
    subtitle: row.subtitle,
    contactOrgName: row.contactOrgName,
    imageUrl: row.imageUrl,
  }))

  /** Ring a directory contact. Mode is chosen per button, not inferred. */
  const callPerson = (person: CallablePerson, mode: "audio" | "video") => {
    setShowNewCall(false)
    useCallsStore.getState().startCall(
      { id: person.id, name: person.name, imageUrl: person.imageUrl },
      mode,
    )
  }

  // Call back a history row over the SDK. Needs the peer's contactPresenceId
  // (1:1 only; group rows carry none → no-op). The app-wide GlobalCallLayer
  // renders the ring/call UI.
  const callFromRecord = (record: CallRecord, mode: "audio" | "video") => {
    if (!record.peerId) return
    useCallsStore.getState().startCall(
      { id: record.peerId, name: record.name, imageUrl: record.imageUrl },
      mode,
    )
  }

  // W14 — calls are a filter view of `messenger.messages WHERE type='call'`
  // (D2). Live updates piggy-back on `useMessengerSubscription` so call rows
  // arriving while the user is on the calls tab append automatically.
  const { calls, hasMore, isLoading, fetchMore } = useCallList()

  const filtered = useMemo(() => {
    return calls.filter(c =>
      !search || c.name.toLowerCase().includes(search.toLowerCase())
    )
  }, [search, calls])

  const selected = useMemo(() => {
    return (selectedId ? calls.find(c => c.id === selectedId) : null) ?? null
  }, [selectedId, calls])

  return (
    <div className="flex h-full w-full bg-card overflow-hidden">
      {/* Left panel: list OR details inline */}
      <div className="flex flex-col h-full overflow-hidden w-full">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-border shrink-0">
          {selected ? (
            <div className="flex items-center gap-2">
              <button
                onClick={() => setSelectedId(null)}
                className="p-1.5 rounded-full hover:bg-muted text-muted-foreground"
                type="button"
              >
                <ArrowLeft className="w-4 h-4" />
              </button>
              <p className="text-base font-semibold text-foreground">{t("presence.callInfo")}</p>
            </div>
          ) : isSearching ? (
            <div className="flex items-center gap-2 flex-1">
              <button
                onClick={() => { setIsSearching(false); setSearch("") }}
                className="p-1.5 rounded-full hover:bg-muted text-muted-foreground"
                type="button"
              >
                <X className="w-4 h-4" />
              </button>
              <input
                autoFocus
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder={t("presence.searchCallsPlaceholder")}
                className="flex-1 bg-transparent text-sm text-foreground placeholder:text-muted-foreground outline-none"
              />
            </div>
          ) : (
            <>
              <h2 className="text-base font-semibold text-foreground">{t("presence.calls")}</h2>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setIsSearching(true)}
                  className="p-2 rounded-full hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                  title="Search"
                  type="button"
                >
                  <Search className="w-4.5 h-4.5" />
                </button>
                <button
                  className="p-2 rounded-full hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                  title={t("presence.newCall")}
                  type="button"
                  onClick={() => setShowNewCall(true)}
                >
                  <PhoneCall className="w-4.5 h-4.5" />
                </button>
              </div>
            </>
          )}
        </div>

        {/* Content: list or details */}
        <div className="flex-1 overflow-y-auto">
          {selected ? (
            <CallInfoPanel
              record={selected}
              onClose={() => setSelectedId(null)}
              onMessage={() => {
                // Was `setSelectedId(null)` alone: the details panel closed and
                // nothing else happened, which read as a dead button. A group
                // call has no single peer to open a chat with, so the record
                // carries no peerId and there is nothing to do.
                if (!selected.peerId) return
                onOpenChat?.({
                  id: selected.peerId,
                  name: selected.name,
                  imageUrl: selected.imageUrl,
                })
                setSelectedId(null);
              }}
              onAudioCall={() => callFromRecord(selected, "audio")}
              onVideoCall={() => callFromRecord(selected, "video")}
            />
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center px-8 py-16">
              <Phone className="w-12 h-12 text-muted-foreground/30 mb-4" strokeWidth={1} />
              <p className="text-sm font-medium text-foreground/60">
                {search ? `No results for "${search}"` : t("presence.noCallsFound")}
              </p>
              {!search && (
                <p className="text-xs text-muted-foreground mt-1">Tap the phone icon above to start a call</p>
              )}
            </div>
          ) : (
            <>
              {filtered.map(record => (
                <CallRow
                  key={record.id}
                  record={record}
                  isSelected={selectedId === record.id}
                  onSelect={() => setSelectedId(record.id)}
                  onCall={(mode) => callFromRecord(record, mode)}
                />
              ))}
              {hasMore && (
                <button
                  type="button"
                  onClick={() => { void fetchMore() }}
                  disabled={isLoading}
                  className="w-full text-center text-xs text-muted-foreground hover:text-foreground py-3 disabled:opacity-50"
                >
                  {isLoading ? "Loading…" : "Load older calls"}
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* New Call picker */}
      {showNewCall && (
        <Dialog open={showNewCall} onOpenChange={setShowNewCall}>
          <DialogContent className="max-w-sm p-0 overflow-hidden">
            <DialogHeader className="px-4 pt-4 pb-2 border-b border-border">
              <DialogTitle className="text-base">New Call</DialogTitle>
            </DialogHeader>
            <div className="px-4 py-2 border-b border-border">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input
                  autoFocus
                  value={peopleQuery}
                  onChange={(e) => setPeopleQuery(e.target.value)}
                  placeholder={t("presence.searchContacts")}
                  className="w-full rounded-md border border-border bg-background py-2 pl-8 pr-3 text-sm outline-none focus:border-primary"
                />
              </div>
            </div>
            <DirectoryScopeTabs value={peopleScope} onChange={setPeopleScope} />
            <div className="max-h-80 overflow-y-auto">
              {peopleLoading && people.length === 0 ? (
                <p className="px-4 py-6 text-center text-sm text-muted-foreground">
                  {t("common.loading")}
                </p>
              ) : people.length === 0 ? (
                <p className="px-4 py-6 text-center text-sm text-muted-foreground">
                  {t("presence.noContactsFound")}
                </p>
              ) : (
                <>
                  {people.map(person => (
                    <div
                      key={person.id}
                      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-muted/50 transition-colors"
                    >
                      <Avatar name={person.name} imageUrl={person.imageUrl} size={40} />
                      <div className="flex-1 min-w-0 text-left">
                        <p className="text-sm font-medium text-foreground truncate">{person.name}</p>
                        <p className="text-xs text-muted-foreground truncate">{person.subtitle}</p>
                        {person.contactOrgName && person.contactOrgName !== person.subtitle && (
                          <p className="text-xs text-muted-foreground truncate">{person.contactOrgName}</p>
                        )}
                      </div>
                      {/* Two buttons, not one. The old row inferred the mode from
                          the history entry it was built from, so the choice
                          belonged to a past call rather than to this one. */}
                      <button
                        type="button"
                        onClick={() => callPerson(person, "audio")}
                        title={t("presence.audioCall")}
                        aria-label={`${t("presence.audioCall")} - ${person.name}`}
                        className="p-2 rounded-full text-primary hover:bg-primary/10 transition-colors shrink-0"
                      >
                        <Phone className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => callPerson(person, "video")}
                        title={t("presence.videoCall")}
                        aria-label={`${t("presence.videoCall")} - ${person.name}`}
                        className="p-2 rounded-full text-primary hover:bg-primary/10 transition-colors shrink-0"
                      >
                        <Video className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  <LoadMoreSentinel hasMore={peopleHasMore} loading={peopleLoadingMore} onVisible={loadMorePeople} />
                </>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}

    </div>
  )
}

function CallRow({ record, isSelected, onSelect, onCall }: { record: CallRecord; isSelected: boolean; onSelect: () => void; onCall: (mode: "audio" | "video") => void }) {
  const t = useTranslation()
  return (
    <div
      className={`group flex items-center gap-3 px-4 py-3 transition-colors cursor-pointer ${isSelected ? "bg-primary/8" : "hover:bg-muted/50"}`}
      onClick={onSelect}
    >
      <div className="relative shrink-0">
        <Avatar name={record.name} imageUrl={record.imageUrl} contactId={record.peerId} />
        {record.isOnline && (
          <span className="absolute bottom-0.5 right-0.5 w-2.5 h-2.5 rounded-full bg-green-500 border-2 border-background" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-medium truncate ${record.isMissedCall ? "text-destructive" : "text-foreground"}`}>
          {record.name}
        </p>
        <div className="flex items-center gap-1.5 mt-0.5">
          <CallIcon record={record} />
          <span className="text-xs text-muted-foreground truncate">{record.time}</span>
          {record.duration && (
            <span className="text-xs text-muted-foreground">· {record.duration}</span>
          )}
        </div>
      </div>
      <button
        className="p-2 rounded-full hover:bg-muted text-muted-foreground hover:text-foreground transition-all shrink-0 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-within:opacity-100"
        title={record.isVideoCall ? t("presence.videoAction") : t("presence.callAction")}
        onClick={e => { e.stopPropagation(); onCall(record.isVideoCall ? "video" : "audio") }}
        type="button"
      >
        {record.isVideoCall ? <Video className="w-4.5 h-4.5" /> : <Phone className="w-4.5 h-4.5" />}
      </button>
    </div>
  )
}
